/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";

import { api as shiftsApi } from "store/api/shiftsApi";

import { ClaimType, ShiftClaim, ShiftClaimsPollingState, ShiftClaimsStatus } from "./types";

const initialState: ShiftClaimsPollingState = {
    shiftClaims: [],
    shiftClaimsNotifications: [],
};

export const shiftClaimsPollingSlice = createSlice({
    initialState,
    name: "shiftClaimsPolling",
    reducers: {
        setShiftClaims: (state, action: PayloadAction<ShiftClaim>) => {
            state.shiftClaims = [...state.shiftClaims, action.payload];
        },
        clearShiftNotificationById: (state, action: PayloadAction<{ shiftId: string }>) => {
            state.shiftClaimsNotifications = state.shiftClaimsNotifications.filter(
                (shift) => shift.shiftId !== action.payload.shiftId
            );
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            shiftsApi.endpoints.getShiftClaimsStatuses.matchFulfilled,
            (state, { payload }) => {
                const newShiftClaims = state.shiftClaims.filter((stateShiftClaim) => {
                    return !payload?.some(
                        (responseShiftClaim) =>
                            stateShiftClaim.shiftId === responseShiftClaim.shiftId &&
                            stateShiftClaim.claimType === responseShiftClaim.claimType &&
                            (responseShiftClaim.status === ShiftClaimsStatus.Success ||
                                responseShiftClaim.status === ShiftClaimsStatus.Failure)
                    );
                });
                const notPendingShifts = payload.filter(
                    ({ status }) => status !== ShiftClaimsStatus.Pending
                );
                return isEqual(newShiftClaims, state.shiftClaims)
                    ? {
                          ...state,
                          shiftClaimsNotifications: notPendingShifts,
                      }
                    : {
                          ...state,
                          shiftClaims: newShiftClaims,
                          shiftClaimsNotifications: notPendingShifts,
                      };
            }
        );
        builder.addMatcher(shiftsApi.endpoints.unApplyShift.matchFulfilled, (state, { payload }) => {
            state.shiftClaims = [
                ...state.shiftClaims,
                {
                    shiftId: payload.shift_id,
                    status: ShiftClaimsStatus.Pending,
                    claimType: ClaimType.Unclaim,
                },
            ];
        });
        builder.addMatcher(shiftsApi.endpoints.reserveShift.matchFulfilled, (state, { payload }) => {
            state.shiftClaims = [
                ...state.shiftClaims,
                {
                    shiftId: payload.shiftId,
                    status: ShiftClaimsStatus.Pending,
                    claimType: ClaimType.Claim,
                },
            ];
        });
    },
});

export const { setShiftClaims, clearShiftNotificationById } = shiftClaimsPollingSlice.actions;

const shiftClaimsState = (state: any) => state.shiftClaimsPolling;

export const shiftClaimsSelector = createSelector(
    shiftClaimsState,
    ({ shiftClaims }) => shiftClaims
);

export const shiftClaimsNotificationsSelector = createSelector(
    shiftClaimsState,
    ({ shiftClaimsNotifications }) => shiftClaimsNotifications
);

export default shiftClaimsPollingSlice.reducer;
